import React from "react";
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Grid,
  Typography,
  Container,
  Card,
  CardContent,
  CardMedia,
  Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import {
  FaHandshake,
  FaComments,
  FaBuilding,
  FaGlobe,
  FaUsers,
  FaShieldAlt,
} from "react-icons/fa";
import contactBg from "@assets/images/contactbg.jpg";
import homebanner from "@assets/images/IMG_70531.jpg";
import ContactSection from "../Home/contactSection";
const DESCO_API_BASE = import.meta.env.VITE_DESCO_API_BASE;
import SEO from "../../../utils/SeoComponent";
import AreasWeServe from "./Areas-we-serve-section";
import LetsBuildYourFuture from "./Lets-build-your-future";
import AgentsSection from "./Agents-section";
import WhatWeDo from "./What-we-do-section";
import WhatWeAre from "./Who-we-are-section";

// Styled Components (reusing some from Services page)
const PageSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  position: "relative",
  overflow: "hidden",
  [theme.breakpoints.down("sm")]: {
    paddingTop: theme.spacing(4), // 32px top padding
    paddingBottom: theme.spacing(4),
  },
}));

const HeroSection = styled(PageSection)(({ theme }) => ({
  backgroundColor: "#f8f9fa",
  backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(${homebanner})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  color: "white",
  padding: theme.spacing(15, 0),
  textAlign: "center",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "150px",
    background: "linear-gradient(to top, #fff, transparent)",
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  position: "relative",
  fontWeight: 700,
  paddingBottom: theme.spacing(2),
  display: "inline-block",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "80px",
    height: "4px",
    backgroundColor: "#C10E14",
  },
}));

const ContentBox = styled(Box)(({ theme }) => ({
  backgroundColor: "#fff",
  borderRadius: "15px",
  padding: theme.spacing(5),
  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(3),
  },
}));

const FeatureIcon = styled(Box)(({ theme }) => ({
  fontSize: "2.5rem",
  color: "#C10E14",
  marginBottom: theme.spacing(2),
  marginRight: theme.spacing(2),
}));

const About = () => {
  const [agentDetails, setAgentDetails] = useState([]);
  const [aboutus, setAboutUs] = useState([]);

  useEffect(() => {
    const fetchAboutUs = async () => {
      try {
        const res = await axios.get(`${DESCO_API_BASE}/about/index`);
        setAboutUs(res.data?.data || []);
      } catch (err) {
        console.error("Error fetching services:", err);
      }
    };

    fetchAboutUs();
  }, []);

  const features = [
    {
      icon: <FaHandshake />,
      title: "Decades of Market Expertise",
      description:
        "With more than 20 years in the industry, our team has a deep understanding of Dubai's real estate market cycles, regulations and emerging trends. We don't just follow the market—we help shape it.",
    },
    {
      icon: <FaUsers />,
      title: "Best Real Estate Agents in Dubai",
      description:
        "Our agents are multilingual, RERA certified and trained to provide comprehensive recommendations and ethical service. We pride ourselves on having the finest real estate professionals in the UAE.",
    },
    {
      icon: <FaGlobe />,
      title: "Global Clientele, Local Expertise",
      description:
        "From GCC investors to expatriate families and international buyers, our clients come from around the world. Our team bridges the gap between international expectations and local regulations.",
    },
    {
      icon: <FaBuilding />,
      title: "Tailored Services for Every Client",
      description:
        "We don't believe in a one-size-fits-all approach. Whether you're investing, relocating, or selling, we create a strategy tailored to your goals, timeline, and budget.",
    },
    {
      icon: <FaShieldAlt />,
      title: "Transparent, Ethical & Results-Driven",
      description:
        "We build trust through honesty and consistent performance. Our success is measured not only by transactions but by the long-term relationships we maintain with our clients.",
    },
    {
      icon: <FaComments />,
      title: "End-to-End Real Estate Services",
      description:
        "From luxury property sales to investment opportunities, our expertise covers every aspect of Dubai's thriving property landscape with unmatched service quality.",
    },
  ];

  const areas = [
    "Palm Jebel Ali",
    "Downtown Dubai",
    "Dubai Marina",
    "Dubai Hills Estate",
    "Business Bay",
    "Jumeirah Village Circle (JVC)",
    "Emaar South & Dubai Creek Harbour",
  ];

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const response = await axios.get(`${DESCO_API_BASE}/agents/index`);
        setAgentDetails(response.data.data);
      } catch (error) {
        console.error("Error fetching agents:", error);
      }
    };

    fetchAgents();
  }, []);

  return (
    <>
      <SEO slug={"aboutus"} />
      <Box className="about-page">
        {/* Hero Section */}
        <HeroSection className="hero-section">
          <Container
            maxWidth="md"
            sx={{
              mt: { xs: 6, md: 14 },
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                fontWeight={700}
              >
                About DESCO
              </Typography>
              <Typography
                variant="h5"
                component="p"
                sx={{ mb: 4, opacity: 0.9 }}
              >
                Decades of Excellence in Real Estate and Beyond
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  const element = document.getElementById("about-content");
                  if (element) {
                    element.scrollIntoView({ behavior: "smooth" });
                  }
                }}
                sx={{
                  borderRadius: "50px",
                  py: 1.5,
                  px: 4,
                  fontSize: "1rem",
                  boxShadow: "0 4px 14px rgba(0, 0, 0, 0.25)",
                  backgroundColor: "#C10E14",
                  "&:hover": {
                    backgroundColor: "#A30B11",
                  },
                }}
              >
                Our Story
              </Button>
            </motion.div>
          </Container>
        </HeroSection>
        {/* About Content Section */}
        <PageSection id="about-content" sx={{ bgcolor: "#f8f9fc" }}>
          <Container>
            <ContentBox>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8 }}
              >
                <SectionTitle
                  variant="h4"
                  component="h2"
                  sx={{ marginBottom: 3 }}
                >
                  Welcome to DESCO Real Estate
                </SectionTitle>

                {/* Render dynamic content if available */}
                {aboutus.about?.value && (
                  <Typography
                    variant="body1"
                    dangerouslySetInnerHTML={{ __html: aboutus.about?.value }}
                    sx={{
                      fontSize: "18px",
                      lineHeight: 1.7,
                      mb: 3,
                    }}
                  />
                )}
              </motion.div>
            </ContentBox>
          </Container>
        </PageSection>
        {/* Who We Are Section */}
        <WhatWeAre />
        {/* What We Do Section */}
        {/* <WhatWeDo /> */}
        {/* Why Choose Section */}
        <PageSection sx={{ bgcolor: "#fff" }}>
          <Container>
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <SectionTitle variant="h4" component="h2">
                Why Choose DESCO?
              </SectionTitle>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ maxWidth: "700px", mx: "auto", mt: 2 }}
              >
                Your dreams deserve more than just another real estate agent
              </Typography>
            </Box>

            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", p: 2 }}
                    >
                      <FeatureIcon>{feature.icon}</FeatureIcon>
                      <Box>
                        <Typography
                          variant="h6"
                          component="h3"
                          fontWeight={600}
                          gutterBottom
                        >
                          {feature.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ lineHeight: 1.6 }}
                        >
                          {feature.description}
                        </Typography>
                      </Box>
                    </Box>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </PageSection>
        {/* Vision & Mission Section */}
        {/* <PageSection sx={{ bgcolor: "#f8f9fc" }}>
          <Container>
            <Grid container spacing={6}>
              <Grid item xs={12} md={6}>
                <ContentBox>
                  <motion.div
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8 }}
                  >
                    <SectionTitle variant="h5" component="h3" sx={{ mb: 3 }}>
                      Our Vision
                    </SectionTitle>
                    <Typography
                      variant="body1"
                      sx={{ fontSize: "16px", lineHeight: 1.7 }}
                    >
                      To be the most trusted and admired real estate company in
                      Dubai—recognised for delivering exceptional value,
                      innovation, and integrity across every property
                      transaction.
                    </Typography>
                  </motion.div>
                </ContentBox>
              </Grid>
              <Grid item xs={12} md={6}>
                <ContentBox>
                  <motion.div
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8 }}
                  >
                    <SectionTitle variant="h5" component="h3" sx={{ mb: 3 }}>
                      Our Mission
                    </SectionTitle>
                    <Typography
                      variant="body1"
                      sx={{ fontSize: "16px", lineHeight: 1.7 }}
                    >
                      To empower clients through informed decisions,
                      professional guidance, and world-class real estate
                      services that enhance lives and generate long-term value.
                    </Typography>
                  </motion.div>
                </ContentBox>
              </Grid>
            </Grid>
          </Container>
        </PageSection> */}
        {/* Areas We Serve Section */}\
        {/* <AreasWeServe /> */}

        {/* Excellence Details Section - Keep if API provides data */}
        {aboutus?.excellence && aboutus.excellence.length > 0 && (
          <PageSection sx={{ bgcolor: "#f8f9fc" }}>
            <Container>
              <ContentBox>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <Typography
                    variant="h5"
                    component="h3"
                    gutterBottom
                    fontWeight={600}
                  >
                    Excellence in Every Detail
                  </Typography>
                  <Grid container spacing={0} sx={{ mt: 2 }}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body1" component="div">
                        <ul>
                          {aboutus?.excellence
                            ?.slice(0, 3)
                            .map((point, index) => (
                              <li key={index} style={{ marginBottom: "16px" }}>
                                <Typography
                                  variant="subtitle1"
                                  fontWeight={600}
                                >
                                  {point?.title}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {point?.subtitle}
                                </Typography>
                              </li>
                            ))}
                        </ul>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body1" component="div">
                        <ul>
                          {aboutus?.excellence
                            ?.slice(3, 6)
                            .map((point, index) => (
                              <li key={index} style={{ marginBottom: "16px" }}>
                                <Typography
                                  variant="subtitle1"
                                  fontWeight={600}
                                >
                                  {point?.title}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {point?.subtitle}
                                </Typography>
                              </li>
                            ))}
                        </ul>
                      </Typography>
                    </Grid>
                  </Grid>
                </motion.div>
              </ContentBox>
            </Container>
          </PageSection>
        )}
        {/* Agents Section */}
        <AgentsSection agentDetails={agentDetails} />
        {/* {lets build your future section} */}
        {/* <LetsBuildYourFuture /> */}
        {/* Contact Section */}
        <Box
          style={{
            padding: "80px 0px",
            backgroundImage: `url(${contactBg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        >
          <ContactSection />
        </Box>
      </Box>
    </>
  );
};

export default About;
